import React, { useState } from "react";
import "./GridCommandV4.scss";

/**
 * Grid Command V4 - Evolution of the clean command center
 * Focus: Less stats, more visual game elements, brand drops as events
 */
const GridCommandV4: React.FC = () => {
    const [selectedGame, setSelectedGame] = useState<string>("all");
    const [searchQuery, setSearchQuery] = useState("");
    const [showGameSelector, setShowGameSelector] = useState(false);

    // Enhanced game data with logos
    const games = [
        {
            id: "valorant",
            name: "Valorant",
            logo: "/images/games/valorant-logo.png",
            activeQuests: 5,
            brandDrops: 2,
            color: "#FF4655",
        },
        {
            id: "fortnite",
            name: "Fortnite",
            logo: "/images/games/fortnite-logo.png",
            activeQuests: 8,
            brandDrops: 3,
            color: "#318CFE",
        },
        {
            id: "apex",
            name: "Apex Legends",
            logo: "/images/games/apex-logo.png",
            activeQuests: 4,
            brandDrops: 1,
            color: "#DA292A",
        },
        {
            id: "cod",
            name: "Call of Duty",
            logo: "/images/games/cod-logo.png",
            activeQuests: 6,
            brandDrops: 2,
            color: "#00A652",
        },
    ];

    return (
        <div className="grid-command-v4">
            {/* Streamlined Header */}
            <div className="command-header">
                <div className="user-quick">
                    <img src="/placeholder-avatar.jpg" alt="User" className="user-avatar" />
                    <div className="user-info">
                        <span className="user-level">LVL 42</span>
                        <div className="xp-mini-bar">
                            <div className="xp-fill" style={{width: "67%"}} />
                        </div>
                    </div>
                    <div className="fuel-display">
                        <span className="fuel-icon">⚡</span>
                        <span className="fuel-amount">1,058</span>
                    </div>
                </div>

                <div className="status-center">
                    <div className="live-indicator">
                        <span className="live-dot" />
                        <span className="live-text">LIVE SESSION</span>
                    </div>
                </div>

                <div className="header-actions">
                    <div className="guild-quick">
                        <span className="guild-rank">#7</span>
                        <span className="guild-name">APEX PREDATORS</span>
                    </div>
                    <button className="notifications">
                        <span className="notif-icon">🔔</span>
                        <span className="notif-count">3</span>
                    </button>
                </div>
            </div>

            {/* Game Bar - Visual Priority */}
            <div className="game-bar">
                <div className="game-tiles">
                    <button
                        className={`game-tile ${selectedGame === "all" ? "active" : ""}`}
                        onClick={() => setSelectedGame("all")}
                    >
                        <div className="tile-inner">
                            <span className="all-games-icon">🎮</span>
                            <span className="tile-label">ALL</span>
                            {selectedGame === "all" && <span className="active-indicator" />}
                        </div>
                    </button>

                    {games.map(game =>
                        <button
                            key={game.id}
                            className={`game-tile ${selectedGame === game.id ? "active" : ""}`}
                            onClick={() => setSelectedGame(game.id)}
                        >
                            <div className="tile-inner">
                                <img src={game.logo} alt={game.name} className="game-logo" />
                                {game.brandDrops > 0 &&
                  <span className="brand-drop-indicator">{game.brandDrops}</span>
                                }
                                {selectedGame === game.id && <span className="active-indicator" />}
                            </div>
                        </button>)}
                </div>

                <button className="game-search" onClick={() => setShowGameSelector(!showGameSelector)}>
                    <span className="search-icon">🔍</span>
                </button>
            </div>

            {/* Main Grid */}
            <div className="command-grid">

                {/* LEFT: Simplified Stats & Actions */}
                <div className="panel panel--sidebar">
                    {/* Key Stats Only */}
                    <div className="stat-summary">
                        <div className="stat-block">
                            <span className="stat-icon">🎯</span>
                            <div className="stat-info">
                                <span className="stat-value">12</span>
                                <span className="stat-label">ACTIVE</span>
                            </div>
                        </div>
                        <div className="stat-block">
                            <span className="stat-icon">🔥</span>
                            <div className="stat-info">
                                <span className="stat-value">67%</span>
                                <span className="stat-label">WIN RATE</span>
                            </div>
                        </div>
                    </div>

                    {/* Recent Achievement Showcase */}
                    <div className="achievement-showcase">
                        <div className="showcase-header">
                            <span className="new-badge">NEW</span>
                            <span className="showcase-title">LATEST UNLOCK</span>
                        </div>
                        <div className="achievement-hero">
                            <div className="achievement-icon-large">🎯</div>
                            <div className="achievement-name">SHARPSHOOTER</div>
                            <div className="achievement-desc">100 headshots</div>
                        </div>
                    </div>

                    {/* Guild Widget */}
                    <div className="guild-widget">
                        <div className="guild-header">
                            <span className="guild-icon">⚔️</span>
                            <span className="guild-title">GUILD WAR</span>
                        </div>
                        <div className="war-status">
                            <div className="position-indicator">
                                <span className="position">#7</span>
                                <span className="movement">↑2</span>
                            </div>
                            <div className="war-timer">
                                <span className="timer-label">ENDS IN</span>
                                <span className="timer-value">2H 14M</span>
                            </div>
                        </div>
                        <button className="guild-action">CONTRIBUTE</button>
                    </div>

                    {/* Quick Play */}
                    <button className="play-button">
                        <span className="play-icon">▶</span>
                        <span className="play-text">QUICK PLAY</span>
                    </button>
                </div>

                {/* MIDDLE: Mission Hub */}
                <div className="panel panel--missions">
                    {/* Brand Drop Hero */}
                    <div className="brand-drop-hero">
                        <div className="drop-live-badge">
                            <span className="live-dot" />
                            <span>LIVE DROP</span>
                        </div>
                        <div className="drop-content">
                            <img src="/images/brands/redbull-logo.png" alt="Red Bull" className="brand-logo" />
                            <h2 className="drop-title">CLUTCH CHALLENGE</h2>
                            <div className="drop-games">
                                <img src="/images/games/valorant-logo.png" alt="Valorant" className="game-mini" />
                                <img src="/images/games/apex-logo.png" alt="Apex" className="game-mini" />
                                <span className="more-games">+2</span>
                            </div>
                            <div className="drop-rewards">
                                <div className="reward-bubble">
                                    <span className="reward-amount">2.5K</span>
                                    <span className="reward-type">XP</span>
                                </div>
                                <div className="reward-bubble">
                                    <span className="reward-amount">1K</span>
                                    <span className="reward-type">FUEL</span>
                                </div>
                                <div className="reward-bubble reward-bubble--special">
                                    <span className="reward-icon">💎</span>
                                </div>
                            </div>
                            <div className="drop-footer">
                                <div className="drop-timer">
                                    <span className="timer-dot">●</span>
                                    <span>48H LEFT</span>
                                </div>
                                <button className="drop-cta">ENTER DROP</button>
                            </div>
                        </div>
                    </div>

                    {/* Mission Filters */}
                    <div className="mission-filters">
                        <button className="filter-chip filter-chip--active">
                            <span className="chip-icon">🎮</span>
                            <span className="chip-label">GAME</span>
                            <span className="chip-count">8</span>
                        </button>
                        <button className="filter-chip">
                            <span className="chip-icon">🔥</span>
                            <span className="chip-label">BRAND</span>
                            <span className="chip-count">3</span>
                        </button>
                        <button className="filter-chip">
                            <span className="chip-icon">⚔️</span>
                            <span className="chip-label">GUILD</span>
                            <span className="chip-count">2</span>
                        </button>
                    </div>

                    {/* Mission Cards - Simplified */}
                    <div className="mission-grid">
                        {/* Game Mission */}
                        <div className="mission-compact">
                            <img src="/images/games/valorant-logo.png" alt="Valorant" className="mission-game-logo" />
                            <div className="mission-info">
                                <h4 className="mission-name">ACE MACHINE</h4>
                                <div className="mission-progress-ring">
                                    <svg viewBox="0 0 36 36">
                                        <path className="ring-bg" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                                        <path className="ring-fill" strokeDasharray="40, 100" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                                    </svg>
                                    <span className="progress-label">2/5</span>
                                </div>
                            </div>
                            <div className="mission-reward">
                                <span className="reward-value">750</span>
                                <span className="reward-label">XP</span>
                            </div>
                        </div>

                        {/* Daily Mission */}
                        <div className="mission-compact mission-compact--daily">
                            <div className="daily-icon">☀️</div>
                            <div className="mission-info">
                                <h4 className="mission-name">FIRST BLOOD</h4>
                                <div className="game-checks">
                                    <span className="check check--done">✓</span>
                                    <span className="check">○</span>
                                    <span className="check">○</span>
                                </div>
                            </div>
                            <div className="mission-timer">
                                <span className="timer-urgent">4H</span>
                            </div>
                        </div>

                        {/* Guild Mission */}
                        <div className="mission-compact mission-compact--guild">
                            <div className="guild-icon">⚔️</div>
                            <div className="mission-info">
                                <h4 className="mission-name">SQUAD WINS</h4>
                                <div className="guild-progress">
                                    <span className="progress-text">7/10</span>
                                    <div className="progress-mini-bar">
                                        <div className="progress-fill" style={{width: "70%"}} />
                                    </div>
                                </div>
                            </div>
                            <button className="join-btn">JOIN</button>
                        </div>

                        {/* Brand Mission */}
                        <div className="mission-compact mission-compact--brand">
                            <img src="/images/brands/snapple-logo.png" alt="Snapple" className="brand-mini" />
                            <div className="mission-info">
                                <h4 className="mission-name">SNAPPLE STREAK</h4>
                                <div className="instant-indicators">
                                    <span className="instant-dot instant-dot--won" />
                                    <span className="instant-dot instant-dot--won" />
                                    <span className="instant-dot" />
                                </div>
                            </div>
                            <div className="mission-tag">INSTANT</div>
                        </div>
                    </div>
                </div>

                {/* RIGHT: Activity Stream */}
                <div className="panel panel--activity">
                    <div className="activity-header">
                        <h3>LIVE FEED</h3>
                        <div className="feed-filters">
                            <button className="feed-filter feed-filter--active">ALL</button>
                            <button className="feed-filter">GUILD</button>
                            <button className="feed-filter">YOU</button>
                        </div>
                    </div>

                    <div className="activity-stream">
                        {/* Achievement Alert */}
                        <div className="activity-card activity-card--achievement">
                            <div className="activity-glow" />
                            <div className="activity-icon">🏆</div>
                            <div className="activity-content">
                                <span className="activity-user">You</span>
                                <span className="activity-action">unlocked</span>
                                <span className="activity-highlight">Sharpshooter</span>
                            </div>
                            <span className="activity-time">NOW</span>
                        </div>

                        {/* Guild Update */}
                        <div className="activity-card activity-card--guild">
                            <div className="activity-icon">⬆️</div>
                            <div className="activity-content">
                                <span className="activity-highlight">APEX PREDATORS</span>
                                <span className="activity-action">climbed to</span>
                                <span className="activity-rank">#6</span>
                            </div>
                            <span className="activity-time">5m</span>
                        </div>

                        {/* Friend Activity */}
                        <div className="activity-card">
                            <div className="activity-icon">🎮</div>
                            <div className="activity-content">
                                <span className="activity-user">DeadEye</span>
                                <span className="activity-action">completed</span>
                                <span className="activity-quest">ACE MACHINE</span>
                            </div>
                            <span className="activity-time">12m</span>
                        </div>

                        {/* Reward Claim */}
                        <div className="activity-card activity-card--reward">
                            <div className="activity-icon">💰</div>
                            <div className="activity-content">
                                <span className="activity-action">Claimed</span>
                                <span className="activity-highlight">500 FUEL</span>
                                <span className="activity-action">from daily</span>
                            </div>
                            <span className="activity-time">1h</span>
                        </div>
                    </div>

                    {/* Upcoming Events Widget */}
                    <div className="events-widget">
                        <h4 className="widget-title">NEXT UP</h4>
                        <div className="event-preview">
                            <div className="event-icon">🎯</div>
                            <div className="event-details">
                                <span className="event-name">Double XP Weekend</span>
                                <span className="event-timer">Starts in 8H</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default GridCommandV4;